import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import CreatorHeader from '../../components/creator/CreatorHeader';

type VideoCampaign = {
  id: string;
  video_id: string;
  campaign_id: string;
  placement?: { timestamp: number; ad_type: string };
  ad_type: string;
  processing_status: string;
  processing_progress: number;
  processed_video_url?: string;
  ai_suggestions?: any[];
  ai_analysis?: any;
  published_url?: string;
  published_status?: string;
  views_min?: number;
  track_until?: string;
  views?: { cnt?: number; ts?: string; err?: string };
};

let status2lbl: Record<string, string> = {
  tracking: 'Tracking views',
  approved: 'Approved',
  rejected: 'Rejected',
  pending_review: 'Pending review',
};

let date2lbl = (d?: string)=>d ? new Date(d).toLocaleString() : '-';

export default function VideoProcessing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoCampaignId = searchParams.get('id');
  const videoId = searchParams.get('video_id');
  const campaignId = searchParams.get('campaign_id');

  const [videoCampaign, setVideoCampaign] = useState<VideoCampaign | null>(null);
  const [video, setVideo] = useState<any>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (videoCampaignId) {
      fetchAllData();
    }
  }, [videoCampaignId]);

  useEffect(() => {
    if (videoCampaign?.processing_status === 'analyzing' || 
        videoCampaign?.processing_status === 'processing' ||
        videoCampaign?.processing_status === 'pending' ||
        videoCampaign?.processing_status === 'ready_to_process') {
      
      const interval = setInterval(() => {
        fetchAllData();
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [videoCampaign?.processing_status]);

  const fetchAllData = async () => {
    try {
      const statusResult = await back_app.vcamp_get(videoCampaignId!);

      if (statusResult.err) {
        console.error('❌ Status fetch failed:', statusResult.err);
        setLoading(false);
        return;
      }

      const vc = statusResult;
      setVideoCampaign(vc);

      if (videoId && !video) {
        const videoResult = await back_app.video_get(videoId);
        if (!videoResult.err) {
          setVideo(videoResult);
        }
      }

      if (campaignId) {
        const campaignResult = await back_app.campaign_get(campaignId);
        if (!campaignResult.err) {
          setCampaign(campaignResult);
        } else {
          console.error('❌ Campaign fetch failed:', campaignResult);
        }
      }
    } catch (error) {
      console.error('❌ Error:', error);
      if ((error as Error).message == 'no session') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = () => {
    if (!videoCampaign?.id)
      return;
    navigate(`/creator/publish-social?id=${videoCampaign.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!videoCampaign || !video) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing job not found</h2>
            <button
              onClick={() => navigate('/creator/videos')}
              className="text-blue-600 hover:underline"
            >
              Back to videos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (videoCampaign.processing_status === 'completed' && videoCampaign.processed_video_url) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button
            onClick={() => navigate('/creator/videos')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <span>←</span>
            <span>Back to Videos</span>
          </button>

          <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                  {video?.title}
                </h3>
                <p className="text-sm text-gray-600">
                  Campaign: {campaign?.name}
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                </svg>
                <span>
                  Ad at {Math.floor((videoCampaign.placement?.timestamp || 0) / 60)}:{String(Math.floor((videoCampaign.placement?.timestamp || 0) % 60)).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          {videoCampaign.published_url && (
            <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Published</h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  {status2lbl[videoCampaign.published_status || '']
                    || videoCampaign.published_status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between gap-4 py-2 border-b">
                  <span className="text-gray-600">URL</span>
                  <a href={videoCampaign.published_url} target="_blank" rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:underline truncate">
                    {videoCampaign.published_url}
                  </a>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-gray-600">Views detected</span>
                  <span className="font-semibold text-gray-900">
                    {(videoCampaign.views?.cnt || 0).toLocaleString()}
                    {videoCampaign.views_min
                      ? ` / ${videoCampaign.views_min.toLocaleString()} min` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-gray-600">Last checked</span>
                  <span className="font-semibold text-gray-900">
                    {date2lbl(videoCampaign.views?.ts)}
                  </span>
                </div>
                {videoCampaign.published_status == 'tracking' && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-gray-600">Tracking until</span>
                    <span className="font-semibold text-gray-900">
                      {date2lbl(videoCampaign.track_until)}
                    </span>
                  </div>
                )}
                {videoCampaign.views?.err && (
                  <div className="py-2 text-red-600">
                    Last view check failed: {videoCampaign.views.err}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-2xl">✨</span>
                  <span>AI-Processed Video</span>
                </h3>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  Ready
                </span>
              </div>

              <div className="relative rounded-lg overflow-hidden bg-black mb-4 mx-auto" style={{ maxWidth: '400px' }}>
                <video
                  src={videoCampaign.processed_video_url}
                  controls
                  className="w-full"
                  style={{ aspectRatio: '9/16' }}
                />
              </div>

              <button
                onClick={handlePublish}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
              >
                <span>Publish to Social</span>
              </button>
            </div>

            <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                </svg>
                <span>Original</span>
              </h3>

              <div className="relative rounded-lg overflow-hidden bg-black mb-4 mx-auto" style={{ maxWidth: '400px' }}>
                <video
                  src={video.video_url}
                  controls
                  className="w-full"
                  style={{ aspectRatio: '9/16' }}
                />
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-semibold text-gray-900">
                    {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-gray-600">Ad Type</span>
                  <span className="font-semibold text-gray-900">
                    {videoCampaign.ad_type === 'break' ? 'Video Break' : 'Logo Overlay'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Status</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                    Processed
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1">AI-Powered Placement</h4>
                <p className="text-sm text-gray-700">
                  Our AI analyzed your video and identified the optimal moment for ad integration based on scene changes, 
                  pacing, and engagement factors. The placement ensures maximum viewer retention and natural content flow.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }

  if (videoCampaign.processing_status === 'analyzing' || 
      videoCampaign.processing_status === 'processing' ||
      videoCampaign.processing_status === 'pending' ||
      videoCampaign.processing_status === 'ready_to_process') {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="bg-white rounded-xl border shadow-sm p-8 sm:p-12">
            <div className="text-center">
              <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6"></div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                {videoCampaign.processing_status === 'analyzing' ? '🤖 Analyzing Video' : '🎬 Processing Video'}
              </h2>
              
              <p className="text-gray-600 text-base sm:text-lg mb-8">
                {videoCampaign.processing_status === 'analyzing' 
                  ? 'AI is finding the perfect moment for your ad...' 
                  : 'Creating your video with integrated ad...'}
              </p>

              {videoCampaign.processing_progress > 0 && (
                <div className="max-w-md mx-auto">
                  <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out"
                      style={{ width: `${videoCampaign.processing_progress}%` }}
                    />
                  </div>
                  <div className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {videoCampaign.processing_progress}%
                  </div>
                </div>
              )}

              <div className="mt-8 text-xs text-gray-500">
                This usually takes 30-60 seconds
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (videoCampaign.processing_status === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="bg-white rounded-xl border-2 border-red-200 p-8 sm:p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Failed</h2>
            <p className="text-gray-600 mb-6">Something went wrong during video processing.</p>
            <button
              onClick={() => navigate('/creator/videos')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg"
            >
              Back to Videos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-xl border shadow-sm p-12">
          <div className="text-center">
            <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h2>
            <p className="text-gray-600 mb-2">Status: {videoCampaign.processing_status}</p>
            <p className="text-xs text-gray-500">Refreshing automatically...</p>
          </div>
        </div>
      </div>
    </div>
  );
}